import type jsPDF from "jspdf";
import type { PdfTemplateDesign } from "@workspace/api-zod/pdf-template";
import type { CompanyInfo, QuoteData } from "./pdf-generator";
import { fitImage, hexRgb, text, tintRgb, type PdfImage } from "./pdf-template-drawing";
import { readableMainColour } from "./pdf-brand-colour";
import { titleFontFamily } from "./pdf-template-fonts";

type Images = { logo: PdfImage | null; artwork: PdfImage | null };
type RGB = [number, number, number];
const WHITE: RGB = [255, 255, 255];
const INK: RGB = [27, 43, 52];

function luminance(rgb: RGB) {
  const c = rgb.map(v => { const n = v / 255; return n <= 0.04045 ? n / 12.92 : ((n + 0.055) / 1.055) ** 2.4; });
  return c[0] * 0.2126 + c[1] * 0.7152 + c[2] * 0.0722;
}
/** Bright brand colours stay in decoration, never illegible yellow-on-white text. */
export function readableBrand(hex: string): RGB {
  const rgb = hexRgb(hex);
  return 1.05 / (luminance(rgb) + 0.05) >= 4.5 ? rgb : INK;
}
export function onBrand(hex: string): RGB {
  return 1.05 / (luminance(hexRgb(hex)) + 0.05) >= 4.5 ? WHITE : [0, 0, 0];
}
function designInk(design: PdfTemplateDesign, hex = design.textColor ?? design.primaryColor) {
  if ((design.pageVersion ?? 0) >= 4) return hexRgb(hex);
  return design.pageVersion === 3 ? readableMainColour(hex) : readableBrand(hex);
}

/** A4 crop-to-fill, not contain. Clip explicitly so landscape assets never bleed into other content. */
export function fullPageImageBounds(width: number, height: number, pageWidth = 210, pageHeight = 297) {
  if (![width, height].every(n => Number.isFinite(n) && n > 0)) throw new Error("Invalid artwork dimensions");
  const scale = Math.max(pageWidth / width, pageHeight / height);
  return { x: (pageWidth - width * scale) / 2, y: (pageHeight - height * scale) / 2, width: width * scale, height: height * scale };
}
function background(doc: jsPDF, design: PdfTemplateDesign, artwork: PdfImage | null) {
  const w = doc.internal.pageSize.getWidth(), h = doc.internal.pageSize.getHeight();
  if ((design.pageVersion ?? 0) >= 5) {
    // Fade only the decorative background, never the logo, text or brand colours.
    doc.saveGraphicsState();
    doc.setFillColor(...WHITE); doc.rect(0, 0, w, h, "F");
    doc.setGState(doc.GState({ opacity: 0.7 }));
  }
  if (artwork) {
    const b = fullPageImageBounds(artwork.width, artwork.height, w, h);
    doc.saveGraphicsState();
    doc.rect(0, 0, w, h); doc.clip(); doc.discardPath();
    doc.addImage(artwork.data, "PNG", b.x, b.y, b.width, b.height);
    doc.restoreGraphicsState();
  } else {
    // A complete page composition even before users choose optional artwork.
    doc.setFillColor(...tintRgb(design.accentColor)); doc.rect(0, 0, w, h, "F");
    doc.setFillColor(...tintRgb(design.primaryColor)); doc.triangle(0, 110, w, 165, w, h, "F");
    doc.setDrawColor(...hexRgb(design.accentColor)); doc.setLineWidth(0.35);
    for (let i = 0; i < 5; i++) {
      doc.line(-30 + i * 45, h, 95 + i * 45, 157);
      doc.line(0, 174 + i * 9, w, 219 + i * 9);
    }
  }
  if ((design.pageVersion ?? 0) >= 5) doc.restoreGraphicsState();
  if (design.layout === "bold") {
    doc.setFillColor(...hexRgb(design.primaryColor)); doc.rect(0, 0, w, 64, "F");
    doc.setFillColor(...hexRgb(design.accentColor)); doc.rect(0, 282, w, h - 282, "F");
  } else if (design.layout === "architect") {
    doc.setDrawColor(...hexRgb(design.primaryColor)); doc.setLineWidth(0.35);
    doc.rect(8, 8, w - 16, h - 16);
  } else {
    doc.setFillColor(...hexRgb(design.primaryColor)); doc.rect(0, 0, w, 5, "F");
    doc.setFillColor(...hexRgb(design.accentColor)); doc.rect(0, h - 5, w, 5, "F");
  }
}
function plate(doc: jsPDF, design: PdfTemplateDesign, x: number, y: number, w: number, h: number) {
  if ((design.pageVersion ?? 0) >= 5) {
    const tint = (white: number) => hexRgb(design.primaryColor).map(v => Math.round(255 * white + v * (1 - white))) as RGB;
    doc.saveGraphicsState();
    doc.setGState(doc.GState({ opacity: 0.07 }));
    doc.setFillColor(...hexRgb(design.primaryColor));
    doc.roundedRect(x, y + 0.8, w, h, 3, 3, "F");
    doc.restoreGraphicsState();
    doc.saveGraphicsState();
    doc.setGState(doc.GState({ opacity: design.pageVersion === 5 ? 0.94 : 1 }));
    doc.setFillColor(...tint(0.95));
    doc.roundedRect(x, y, w, h, 3, 3, "F");
    doc.restoreGraphicsState();
    doc.setDrawColor(...tint(0.8)); doc.setLineWidth(0.22);
    doc.roundedRect(x, y, w, h, 3, 3, "S");
    // A small branded detail replaces the heavy full-width stripe.
    doc.setDrawColor(...hexRgb(design.accentColor)); doc.setLineWidth(0.45);
    doc.line(x + w - 28, y + 4, x + w - 8, y + 4);
    return;
  }
  // Opaque safe areas guarantee contrast regardless of generated-image pixels.
  doc.setFillColor(...WHITE); doc.rect(x, y, w, h, "F");
}
function textHeight(doc: jsPDF, value: string, size: number, maxLines: number, bold = false, family = "helvetica") {
  doc.setFont(family, bold ? "bold" : "normal");
  doc.setFontSize(size);
  return Math.min(maxLines, (doc.splitTextToSize(value, 162) as string[]).length) * size * 0.3528 * 1.3;
}
function identity(doc: jsPDF, company: CompanyInfo | undefined, design: PdfTemplateDesign, logo: PdfImage | null) {
  const bold = design.layout === "bold";
  const showPanel = !logo || !design.hideLogoBackground;
  if (!bold && showPanel) plate(doc, design, 16, 16, 178, 36);
  if (logo) {
    if (bold && showPanel) plate(doc, design, 19, 19, 74, 30);
    // Match the heading text's 24mm inset, not the old logo box's 22mm edge.
    fitImage(doc, logo, design.logoAlignment === "left" ? 24 : 22, 22, 68, 24, design.logoAlignment);
  }
  doc.setTextColor(...(bold && !design.textColor ? onBrand(design.primaryColor) : designInk(design)));
  if (!logo || (design.showCompanyName ?? company?.showCompanyName) !== false) {
    text(doc, company?.name || "Your Company", logo ? 99 : 22, 31, logo ? 89 : 166, design.companyNameSize ?? 13, 3, true,
      design.companyNameFont === "title" ? titleFontFamily(design) : "helvetica");
  }
}
function heading(doc: jsPDF, design: PdfTemplateDesign, label: string, title: string, subtitle?: string) {
  const font = titleFontFamily(design);
  const titleEnd = 96 + textHeight(doc, title, 30, 3, true, font);
  const subtitleY = titleEnd + 3;
  const bottom = subtitle ? subtitleY + textHeight(doc, subtitle, 10, 4) + 8 : titleEnd + 8;
  // Grow safe areas with the wording, rather than hiding the artwork under
  // a mostly empty fixed-height white box for short headings.
  plate(doc, design, 16, 66, 178, bottom - 66);
  if (design.layout === "architect" && (design.pageVersion ?? 0) < 5) {
    doc.setFillColor(...hexRgb(design.primaryColor)); doc.rect(16, 66, 2, bottom - 66, "F");
  }
  doc.setTextColor(...designInk(design, design.textColor ?? ((design.pageVersion ?? 0) >= 3 ? design.primaryColor : design.accentColor)));
  text(doc, label, 24, 79, 162, 9, 1, true);
  doc.setTextColor(...designInk(design));
  text(doc, title, 24, 96, 162, 30, 3, true, font);
  if (subtitle) {
    doc.setTextColor(70, 78, 83);
    text(doc, subtitle, 24, subtitleY, 162, 10, 4);
  }
}

export function drawFullPageCover(doc: jsPDF, quote: QuoteData, company: CompanyInfo | undefined,
  design: PdfTemplateDesign, images: Images, formattedTotal: string) {
  background(doc, design, images.artwork);
  identity(doc, company, design, images.logo);
  heading(doc, design, "PROJECT QUOTATION", design.coverTitle, design.coverSubtitle);
  plate(doc, design, 16, 220, 178, 59);
  if ((design.pageVersion ?? 0) < 5) {
    doc.setFillColor(...hexRgb(design.accentColor)); doc.rect(16, 220, 178, 1.5, "F");
  }
  doc.setTextColor(70, 78, 83);
  text(doc, "PREPARED FOR", 24, 233, 101, 8, 1, true);
  doc.setTextColor(...designInk(design));
  text(doc, quote.client?.name || quote.projectType || "Your project", 24, 243, 101, 13, 2, true);
  doc.setTextColor(70, 78, 83);
  const projectType = quote.projectType?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  text(doc, [projectType, quote.projectAddress].filter(Boolean).join("\n"), 24, 259, 101, 9, 3);
  doc.setTextColor(...designInk(design));
  text(doc, quote.quoteNumber, 137, 233, 49, 9, 2, true);
  const date = quote.createdAt ? new Date(quote.createdAt) : null;
  doc.setTextColor(70, 78, 83);
  if (date && !Number.isNaN(date.getTime())) text(doc, date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }), 137, 249, 49, 8, 1);
  // Issued designs retain their original total; new covers keep pricing inside.
  if ((design.pageVersion ?? 0) >= 6) return;
  text(doc, quote.includeVat ? "TOTAL INCLUDING VAT" : "TOTAL", 137, 260, 49, 7, 1, true);
  doc.setTextColor(...designInk(design));
  // Monetary values shrink to fit but are never ellipsised or recomputed.
  doc.setFont("helvetica", "bold"); doc.setFontSize(15);
  doc.setFontSize(Math.min(15, 15 * 49 / Math.max(1, doc.getTextWidth(formattedTotal))));
  doc.text(formattedTotal, 137, 271);
}

export function drawFullPageClosing(doc: jsPDF, company: CompanyInfo | undefined, design: PdfTemplateDesign, images: Images) {
  background(doc, design, images.artwork);
  identity(doc, company, design, images.logo);
  const font = titleFontFamily(design);
  const messageY = 97 + textHeight(doc, design.thankYouTitle, 30, 3, true, font) + 8;
  const bottom = messageY + textHeight(doc, design.thankYouMessage, 11, 14) + 12;
  plate(doc, design, 16, 66, 178, bottom - 66);
  if (design.layout === "architect" && (design.pageVersion ?? 0) < 5) {
    doc.setFillColor(...hexRgb(design.primaryColor)); doc.rect(16, 66, 2, bottom - 66, "F");
  }
  doc.setTextColor(...designInk(design, design.textColor ?? ((design.pageVersion ?? 0) >= 3 ? design.primaryColor : design.accentColor)));
  text(doc, "BUILT AROUND YOUR PROJECT", 24, 79, 162, 9, 1, true);
  doc.setTextColor(...designInk(design));
  text(doc, design.thankYouTitle, 24, 97, 162, 30, 3, true, font);
  doc.setTextColor(70, 78, 83);
  text(doc, design.thankYouMessage, 24, messageY, 162, 11, 14);
  plate(doc, design, 16, 234, 178, 45);
  if ((design.pageVersion ?? 0) < 5) {
    doc.setFillColor(...hexRgb(design.accentColor)); doc.rect(16, 234, 178, 1.5, "F");
  }
  doc.setTextColor(...designInk(design));
  text(doc, company?.name || "Your Company", 24, 247, 162, 12, 2, true);
  doc.setTextColor(70, 78, 83);
  text(doc, [company?.phone, company?.email, company?.website].filter(Boolean).join("\n"), 24, 262, 162, 9, 3);
}
